import theme from '@/theme';
import styled from '@emotion/styled';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';

const ToolTip = styled(
    ({
        className,
        customBackgroundColor,
        fitContent,
        tooltipStyles,
        ...props
    }: TooltipProps & {
        customBackgroundColor?: string;
        fitContent?: boolean;
        tooltipStyles?: React.CSSProperties;
    }) => <Tooltip {...props} classes={{ popper: className }} />
)(({ customBackgroundColor, fitContent, tooltipStyles }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: fitContent ? 'fit-content' : '40rem',
        fontSize: '1.6rem',
        backgroundColor: customBackgroundColor || theme.colors.secondary[900],
        ...((tooltipStyles as any) || {}),
    },
    ...(customBackgroundColor && {
        [`& .${tooltipClasses.arrow}`]: {
            color: customBackgroundColor,
        },
    }),
}));

const Container = styled.span<{ display?: string; width?: string; height?: string; cursor?: string; disabled?: boolean }>`
    display: ${props => props.display || 'inline-block'};
    ${props => props.width && `width: ${props.width};`}
    ${props => props.height && `height: ${props.height};`}
    ${props => props.cursor && `cursor: ${props.cursor};`}
    ${props => props.disabled && `disabled: ${props.disabled};`}
`;

interface Props extends Omit<TooltipProps, 'children'> {
    containerWidth?: string;
    containerHeight?: string;
    cursor?: 'pointer';
    disabled?: boolean;
    display?: string;
    children?: React.ReactNode;
    customBackgroundColor?: string;
    fitContent?: boolean;
    whitespace?: string;
    tooltipStyles?: React.CSSProperties;
}

export default function ({
    children,
    title,
    containerWidth,
    containerHeight,
    className,
    cursor,
    disabled,
    display,
    onClick,
    tooltipStyles,
    whitespace,
    ...props
}: Props) {
    return (
        // IT'S VERY IMPORTANT TO WRAP TITLE IN DIV, OTHERWISE IT WILL CRASH APP WHEN USER USES LANGUAGES LIKE JAPANESE
        // https://d-id-api.sentry.io/issues/5502714891/events/58877d482f7848b0ba9961369391899c/
        // https://github.com/facebook/react/issues/17256
        <ToolTip
            title={
                title ? (
                    <div
                        style={{
                            whiteSpace: whitespace ?? 'normal',
                        }}>
                        {title}
                    </div>
                ) : undefined
            }
            tooltipStyles={tooltipStyles}
            onClick={disabled ? undefined : onClick}
            {...props}>
            <Container
                width={containerWidth}
                height={containerHeight}
                cursor={disabled ? undefined : cursor}
                disabled={disabled}
                display={display}
                className={className}>
                {children}
            </Container>
        </ToolTip>
    );
}
